import { Document } from 'src/pages/editor/components/documents/slice';
import { EffectType, DocumentEffect, EFFECT_CONFIGS, getEffectChannel } from 'src/config/effects';

/**
 * Get the active effect type from a document
 *
 * @param doc - Document to check
 * @returns Active effect type or null if no effect
 */
export function getActiveEffectType(doc: Document): EffectType | null {
    return doc.effect?.type ?? null;
}

/**
 * Get effect properties from a document
 *
 * @param doc - Document to get properties from
 * @returns Effect properties or null if no effect
 */
export function getEffectProperties(doc: Document): DocumentEffect | null {
    return doc.effect ?? null;
}

/**
 * Get effect channel name from a document (used in XML)
 *
 * @param doc - Document to get channel from
 * @returns Channel name or null if no effect
 */
export function getDocumentEffectChannel(doc: Document): string | null {
    return doc.effect ? getEffectChannel(doc.effect) : null;
}

/**
 * Create a new effect with optional property overrides
 * Useful for creating effects with custom initial values
 *
 * @param type - Effect type to create
 * @param overrides - Optional property overrides
 * @returns New effect object
 */
export function createEffect(
    type: EffectType.ENGRAVING | EffectType.LETTERPRESS | EffectType.GLOW,
    overrides?: Partial<any>,
): DocumentEffect {
    const config = EFFECT_CONFIGS[type];
    const defaultEffect = config.createDefault();
    return { ...defaultEffect, ...overrides } as DocumentEffect;
}

/**
 * Check if a document has any effect applied
 *
 * @param doc - Document to check
 * @returns True if document has an effect
 */
export function hasEffect(doc: Document): boolean {
    return doc.effect !== null && doc.effect !== undefined;
}

/**
 * Check if a document has a specific effect type
 *
 * @param doc - Document to check
 * @param type - Effect type to check for
 * @returns True if document has the specified effect type
 */
export function hasEffectType(doc: Document, type: EffectType): boolean {
    return doc.effect?.type === type;
}
